/**
 * 공지 로딩 — 빠른 경로 우선
 * 1) sessionStorage 캐시 (즉시)
 * 2) /data/notices.json 빌드 스냅샷 (동일 출처 CDN)
 * 3) admin API (백그라운드 갱신·최신 확인)
 */
import { fetchNoticesList } from './codamNoticesApi'

export const CODAM_NOTICES_CACHE_KEY = 'codam_notices_cache_v1'
const SNAPSHOT_URL = '/data/notices.json'

function readCacheRaw() {
    if (typeof window === 'undefined') return null
    try {
        const raw = window.sessionStorage.getItem(CODAM_NOTICES_CACHE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (!parsed || !Array.isArray(parsed.items)) return null
        return parsed
    } catch {
        return null
    }
}

function writeCache(items) {
    if (typeof window === 'undefined' || !Array.isArray(items)) return
    try {
        window.sessionStorage.setItem(
            CODAM_NOTICES_CACHE_KEY,
            JSON.stringify({ items, cachedAt: Date.now() })
        )
    } catch {
        /* quota 등 — 무시 */
    }
}

/** 렌더 직후 동기 읽기 (재방문·같은 탭) */
export function getCachedNoticesSync() {
    return readCacheRaw()?.items ?? null
}

async function fetchSnapshot() {
    const r = await fetch(SNAPSHOT_URL)
    if (!r.ok) throw new Error(`snapshot ${r.status}`)
    const j = await r.json()
    const items = j.items
    if (!Array.isArray(items)) throw new Error('invalid snapshot')
    return items
}

async function fetchFromApi() {
    return fetchNoticesList()
}

/**
 * 목록용 — 캐시·스냅샷을 먼저 쓰고 API로 백그라운드 갱신.
 * @param {{ onUpdate?: (items: object[]) => void }} [opts]
 */
export async function loadNoticesForList(opts = {}) {
    const { onUpdate } = opts
    const cached = getCachedNoticesSync()
    if (cached?.length) {
        revalidateNoticesInBackground(onUpdate)
        return cached
    }

    try {
        const snap = await fetchSnapshot()
        writeCache(snap)
        revalidateNoticesInBackground(onUpdate)
        return snap
    } catch {
        /* 스냅샷 없음(로컬 dev 등) → API */
    }

    const items = await fetchFromApi()
    writeCache(items)
    return items
}

let revalidatePromise = null

/** API 최신본으로 캐시 갱신 (실패해도 UI 유지) */
export function revalidateNoticesInBackground(onUpdate) {
    if (revalidatePromise) {
        if (onUpdate) {
            revalidatePromise.then((items) => items && onUpdate(items)).catch(() => {})
        }
        return revalidatePromise
    }

    revalidatePromise = (async () => {
        try {
            const items = await fetchFromApi()
            writeCache(items)
            onUpdate?.(items)
            return items
        } catch {
            return null
        } finally {
            revalidatePromise = null
        }
    })()

    return revalidatePromise
}

/** 상세 — 캐시/스냅샷에서 id 조회 후 없으면 API */
export async function loadNoticeById(id) {
    if (!id) return null

    const cached = getCachedNoticesSync()
    const fromCache = cached?.find((x) => x.id === id)
    if (fromCache) return fromCache

    try {
        const snap = await fetchSnapshot()
        writeCache(snap)
        const fromSnap = snap.find((x) => x.id === id)
        if (fromSnap) return fromSnap
    } catch {
        /* continue */
    }

    const items = await fetchFromApi()
    writeCache(items)
    return items.find((x) => x.id === id) ?? null
}
